import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
    Search,
    Plus,
    Trash2,
    TrendingUp,
    DollarSign,
    Target,
    Download,
    Loader2
} from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';

const KeywordResearch = () => {
    const [loading, setLoading] = useState(false);
    const [keywords, setKeywords] = useState([]);
    const [results, setResults] = useState([]);

    // Form state
    const [searchMode, setSearchMode] = useState('manual'); // manual or brand-template
    const [manualKeywords, setManualKeywords] = useState('');
    const [brands, setBrands] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [locationCode, setLocationCode] = useState(2528); // Belgium
    const [languageCode, setLanguageCode] = useState('nl');
    const [locationName, setLocationName] = useState('Belgium');
    const [webshopBrands, setWebshopBrands] = useState([]);
    const [selectedWebshopBrand, setSelectedWebshopBrand] = useState('');

    // Preset templates
    const presetTemplates = [
        { value: 'lighting', label: 'lighting' },
        { value: 'lamp', label: 'lamp' },
        { value: 'luminaire', label: 'luminaire (FR)' },
        { value: 'verlichting', label: 'verlichting (NL)' },
        { value: 'hanglamp', label: 'hanglamp (NL)' },
        { value: 'tafellamp', label: 'tafellamp (NL)' },
        { value: 'vloerlamp', label: 'vloerlamp (NL)' },
        { value: 'wandlamp', label: 'wandlamp (NL)' },
    ];

    // Location presets
    const locationPresets = [
        { code: 2056, name: 'Belgium', language: 'nl' },
        { code: 2528, name: 'Netherlands', language: 'nl' },
        { code: 2250, name: 'France', language: 'fr' },
        { code: 2826, name: 'United Kingdom', language: 'en' },
        { code: 2840, name: 'United States', language: 'en' },
    ];

    useEffect(() => {
        document.title = 'Keyword Research - Mappy';
        fetchResults();
    }, []);

    const fetchResults = async () => {
        try {
            const [resultsResponse, brandsResponse] = await Promise.all([
                axiosInstance.get('/api/tracking/keyword-research/'),
                axiosInstance.get('/api/tracking/webshop-brands/all/')
            ]);
            setResults(resultsResponse.data.results || resultsResponse.data);
            setWebshopBrands(brandsResponse.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const handleLocationChange = (code) => {
        const location = locationPresets.find(l => l.code === parseInt(code));
        if (location) {
            setLocationCode(location.code);
            setLocationName(location.name);
            setLanguageCode(location.language);
        }
    };

    const handleResearch = async () => {
        try {
            setLoading(true);

            let payload;

            if (searchMode === 'manual') {
                // Manual keywords mode
                const keywordList = manualKeywords
                    .split('\n')
                    .map(k => k.trim())
                    .filter(k => k.length > 0);

                if (keywordList.length === 0) {
                    alert('Please enter at least one keyword');
                    return;
                }

                payload = {
                    keywords: keywordList,
                    location_code: locationCode,
                    language_code: languageCode,
                    location_name: locationName,
                    template: 'manual',
                    webshop_brand_id: selectedWebshopBrand || null
                };

                const response = await axiosInstance.post(
                    '/api/tracking/keyword-research/research_keywords/',
                    payload
                );

                if (response.data.success) {
                    alert(`Successfully researched ${response.data.results_saved} keywords!`);
                    setManualKeywords('');
                    fetchResults();
                }
            } else {
                // Brand + template mode
                if (brands.length === 0 || templates.length === 0) {
                    alert('Please add at least one brand and one template');
                    return;
                }

                payload = {
                    brands: brands.map(b => b.value),
                    templates: templates.map(t => t.value),
                    location_code: locationCode,
                    language_code: languageCode,
                    location_name: locationName
                };

                const response = await axiosInstance.post(
                    '/api/tracking/keyword-research/generate_brand_keywords/',
                    payload
                );

                if (response.data.success) {
                    alert(`Successfully researched ${response.data.results_saved} keywords!`);
                    fetchResults();
                }
            }

        } catch (error) {
            console.error('Error researching keywords:', error);
            const errorMessage = error.response?.data?.error || 'Failed to research keywords. Please check your input and try again.';
            alert(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const addBrand = (brandName) => {
        if (brandName && !brands.find(b => b.value === brandName)) {
            setBrands([...brands, { value: brandName, label: brandName }]);
        }
    };

    const removeBrand = (brandValue) => {
        setBrands(brands.filter(b => b.value !== brandValue));
    };

    const toggleTemplate = (template) => {
        const exists = templates.find(t => t.value === template.value);
        if (exists) {
            setTemplates(templates.filter(t => t.value !== template.value));
        } else {
            setTemplates([...templates, template]);
        }
    };

    const getCompetitionColor = (competition) => {
        switch (competition) {
            case 'HIGH': return 'bg-red-500';
            case 'MEDIUM': return 'bg-orange-500';
            case 'LOW': return 'bg-green-500';
            default: return 'bg-gray-500';
        }
    };

    const exportToCSV = () => {
        const headers = ['Keyword', 'Search Volume', 'Competition', 'CPC', 'Low Bid', 'High Bid', 'Location', 'Template'];
        const rows = results.map(r => [
            r.keyword,
            r.search_volume || 0,
            r.competition || 'N/A',
            r.cpc || 0,
            r.low_top_of_page_bid || 0,
            r.high_top_of_page_bid || 0,
            r.location_name || '',
            r.template_used || ''
        ]);

        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${cell}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `keyword-research-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
    };

    return (
        <Layout>
            <div className="container mx-auto p-6 space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Keyword Research</h1>
                        <p className="text-muted-foreground mt-1">
                            DataForSEO API integration for search volume analysis
                        </p>
                    </div>
                    {results.length > 0 && (
                        <Button onClick={exportToCSV} variant="outline">
                            <Download className="mr-2 h-4 w-4" />
                            Export CSV
                        </Button>
                    )}
                </div>

                <Separator />

                {/* Research Form */}
                <Card>
                    <CardHeader>
                        <CardTitle>Research Keywords</CardTitle>
                        <CardDescription>
                            Get search volume, competition, and CPC data from Google Ads
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Search Mode Toggle */}
                        <div className="flex gap-2">
                            <Button
                                variant={searchMode === 'manual' ? 'default' : 'outline'}
                                onClick={() => setSearchMode('manual')}
                            >
                                Manual Keywords
                            </Button>
                            <Button
                                variant={searchMode === 'brand-template' ? 'default' : 'outline'}
                                onClick={() => setSearchMode('brand-template')}
                            >
                                Brand + Templates
                            </Button>
                        </div>

                        {/* Location & Language */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">Location</label>
                                <select
                                    className="w-full border rounded-md px-3 py-2"
                                    value={locationCode}
                                    onChange={(e) => handleLocationChange(e.target.value)}
                                >
                                    {locationPresets.map(loc => (
                                        <option key={loc.code} value={loc.code}>
                                            {loc.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">Language</label>
                                <input
                                    type="text"
                                    className="w-full border rounded-md px-3 py-2"
                                    value={languageCode}
                                    onChange={(e) => setLanguageCode(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Tagging (Optional) */}
                        <div>
                            <label className="block text-sm font-medium mb-2">Tag with Brand (Optional)</label>
                            <select
                                className="w-full border rounded-md px-3 py-2"
                                value={selectedWebshopBrand}
                                onChange={(e) => setSelectedWebshopBrand(e.target.value)}
                            >
                                <option value="">Select a brand to tag results with...</option>
                                {webshopBrands.map(brand => (
                                    <option key={brand.id} value={brand.id}>
                                        {brand.name}
                                    </option>
                                ))}
                            </select>
                            <p className="text-sm text-muted-foreground mt-1">
                                Link these keywords to a specific brand in your catalog
                            </p>
                        </div>

                        {/* Manual Keywords Mode */}
                        {searchMode === 'manual' && (
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Keywords (one per line, max 1000)
                                </label>
                                <textarea
                                    className="w-full border rounded-md px-3 py-2 font-mono text-sm"
                                    rows={8}
                                    placeholder="flos lighting&#10;artemide lamp&#10;louis poulsen verlichting"
                                    value={manualKeywords}
                                    onChange={(e) => setManualKeywords(e.target.value)}
                                />
                                <p className="text-sm text-muted-foreground mt-1">
                                    {manualKeywords.split('\n').filter(k => k.trim()).length} keywords
                                </p>
                            </div>
                        )}

                        {/* Brand + Template Mode */}
                        {searchMode === 'brand-template' && (
                            <div className="space-y-4">
                                {/* Brands */}
                                <div>
                                    <label className="block text-sm font-medium mb-2">Brands</label>
                                    <div className="flex gap-2 mb-2">
                                        <input
                                            type="text"
                                            className="flex-1 border rounded-md px-3 py-2"
                                            placeholder="Enter brand name..."
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    addBrand(e.target.value);
                                                    e.target.value = '';
                                                }
                                            }}
                                        />
                                        <Button
                                            type="button"
                                            size="sm"
                                            onClick={(e) => {
                                                const input = e.target.closest('.flex').querySelector('input');
                                                addBrand(input.value);
                                                input.value = '';
                                            }}
                                        >
                                            <Plus className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {brands.map(brand => (
                                            <Badge key={brand.value} variant="secondary" className="pl-2 pr-1">
                                                {brand.label}
                                                <button
                                                    onClick={() => removeBrand(brand.value)}
                                                    className="ml-2 hover:text-destructive"
                                                >
                                                    <Trash2 className="h-3 w-3" />
                                                </button>
                                            </Badge>
                                        ))}
                                    </div>
                                </div>

                                {/* Templates */}
                                <div>
                                    <label className="block text-sm font-medium mb-2">Templates</label>
                                    <div className="flex flex-wrap gap-2">
                                        {presetTemplates.map(template => (
                                            <Badge
                                                key={template.value}
                                                variant={templates.find(t => t.value === template.value) ? 'default' : 'outline'}
                                                className="cursor-pointer"
                                                onClick={() => toggleTemplate(template)}
                                            >
                                                {template.label}
                                            </Badge>
                                        ))}
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-2">
                                        {brands.length} brands × {templates.length} templates = {brands.length * templates.length} keywords
                                    </p>
                                </div>
                            </div>
                        )}

                        <Button
                            onClick={handleResearch}
                            disabled={loading}
                            className="w-full"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Researching Keywords...
                                </>
                            ) : (
                                <>
                                    <Search className="mr-2 h-4 w-4" />
                                    Research Keywords
                                </>
                            )}
                        </Button>
                    </CardContent>
                </Card>

                {/* Results */}
                <div>
                    <h2 className="text-2xl font-bold mb-4">Results ({results.length})</h2>
                    <div className="space-y-4">
                        {results.length === 0 ? (
                            <Card>
                                <CardContent className="flex flex-col items-center justify-center py-12">
                                    <Search className="h-12 w-12 text-muted-foreground mb-4" />
                                    <h3 className="text-lg font-semibold mb-2">No results yet</h3>
                                    <p className="text-muted-foreground text-center">
                                        Research keywords to see search volume and competition data
                                    </p>
                                </CardContent>
                            </Card>
                        ) : (
                            results.map((result) => (
                                <Card key={result.id} className="hover:shadow-md transition-shadow">
                                    <CardContent className="p-6">
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <h3 className="text-lg font-semibold">{result.keyword}</h3>
                                                    {result.competition && (
                                                        <Badge className={getCompetitionColor(result.competition)}>
                                                            {result.competition}
                                                        </Badge>
                                                    )}
                                                    {result.template_used && (
                                                        <Badge variant="outline">{result.template_used}</Badge>
                                                    )}
                                                </div>
                                                <p className="text-sm text-muted-foreground mb-4">
                                                    {result.location_name} • {result.language_code}
                                                </p>

                                                <div className="grid grid-cols-4 gap-4">
                                                    <div>
                                                        <div className="flex items-center gap-1 text-muted-foreground text-xs mb-1">
                                                            <TrendingUp className="h-3 w-3" />
                                                            Search Volume
                                                        </div>
                                                        <div className="text-2xl font-bold">
                                                            {result.search_volume?.toLocaleString() || 'N/A'}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <div className="flex items-center gap-1 text-muted-foreground text-xs mb-1">
                                                            <DollarSign className="h-3 w-3" />
                                                            CPC
                                                        </div>
                                                        <div className="text-2xl font-bold">
                                                            €{result.cpc ?? 'N/A'}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <div className="flex items-center gap-1 text-muted-foreground text-xs mb-1">
                                                            <Target className="h-3 w-3" />
                                                            Low Bid
                                                        </div>
                                                        <div className="text-xl font-semibold">
                                                            €{result.low_top_of_page_bid ?? 'N/A'}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <div className="flex items-center gap-1 text-muted-foreground text-xs mb-1">
                                                            <Target className="h-3 w-3" />
                                                            High Bid
                                                        </div>
                                                        <div className="text-xl font-semibold">
                                                            €{result.high_top_of_page_bid ?? 'N/A'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default KeywordResearch;
